import lscache from "lscache";
import { GailError } from "@/types/api";
import { MainAlertProps } from "@/types/common/alerts";
import { CACHE_KEYS } from "@/utils/common/cache";
import { reportAuthzDeniedResponse } from "@/utils/authz/captureAuthzDenied";
import {
  ApiFetchRawOptions,
  HttpMethod,
  apiFetchRaw,
  logApiError,
  resolveAccessToken,
  safeParseJson,
  safeReadResponseText,
} from "./core";
import { SessionExpiredError } from "./errors";
import { notifyNotAuthorized, parseNotAuthorized } from "./notAuthorized";

/**
 * Caller-preference return-shape variants over {@link apiFetchRaw}.
 *
 *  - {@link apiFetch} — parsed JSON body or `null`.
 *  - {@link apiStatus} — status info only (mutating writes).
 *  - {@link apiFetchWithStatus} — status info plus parsed body.
 *  - {@link apiLive} — `apiFetch` with optional lscache caching.
 *  - {@link showErrorAlert} — UI alert formatting for `GailError` payloads.
 *
 * The raw send + the on-401-refresh-and-retry recipe live in
 * `core.ts` ({@link apiFetchRaw}, `apiFetchWithRefresh`). This file is
 * the JSON / UI layer over that recipe.
 */

/**
 * Meant to fire whenever a backend call returns the trial-usage limit 403, so
 * `TrialUsageProvider` can refetch usage without every caller wiring in a
 * refetch. Nothing dispatches it yet, so that listener is inert — the trial
 * widget only updates on its poll. Tracked in GAIL-961.
 */
export const TRIAL_LIMIT_EVENT = "gail:trial-limit-reached";

// ---------------------------------------------------------------------------
// Shared option types
// ---------------------------------------------------------------------------

export interface HandleErrorAlert {
  defaultError: string;
  t: (val: string) => string;
  setAlert: (settings: MainAlertProps) => void;
}

export interface ApiFetchOptions<T> extends ApiFetchRawOptions {
  /** Return this value when the response is non-2xx. */
  fallback?: T;
  /** Suppress error-level logging on failure. */
  silent?: boolean;
  /**
   * When `true` and the response is non-2xx, attempt to parse and return
   * the body anyway (mirrors the legacy `alwaysReturn`).
   */
  alwaysReturn?: boolean;
  /** When set, surface non-2xx response bodies as an alert. */
  handleError?: HandleErrorAlert;
  /** When `true`, skip JSON parsing and return `null` (mirrors legacy `doNotParse`). */
  doNotParse?: boolean;
}

export interface ApiStatusResult {
  success: boolean;
  status: number;
  statusText: string;
}

export interface ApiResponseWithStatus<T> extends ApiStatusResult {
  data: T | null;
}

// ---------------------------------------------------------------------------
// Variants
// ---------------------------------------------------------------------------

/**
 * JSON variant of {@link apiFetchRaw}. Returns the parsed body as `T`,
 * or `null` on failure (matching the legacy `apiFetch` contract).
 *
 * Carries the legacy option set so existing callsites can migrate
 * without behavioural changes: `fallback`, `silent`, `alwaysReturn`,
 * `handleError`, `doNotParse`, `revalidateSeconds`.
 */
export async function apiFetch<T = unknown>(
  url: string,
  method: HttpMethod = "GET",
  options: ApiFetchOptions<T> = {},
): Promise<T | null> {
  const { token } = await resolveAccessToken(options.accessToken);

  try {
    const response = await apiFetchRaw(url, method, options);

    if (!response.ok) {
      const responseText = await safeReadResponseText(response);
      notifyNotAuthorized(response.status, responseText);
      reportAuthzDeniedResponse(response.status, responseText, url);

      if (options.fallback !== undefined) return options.fallback;

      if (!options.silent) {
        logApiError("Request failed", {
          url,
          method,
          status: response.status,
          statusText: response.statusText,
          responseText,
          token,
        });
      }

      if (options.handleError) {
        handleErrorAlert(responseText, response.status, options.handleError);
      }

      if (options.alwaysReturn) {
        return safeParseJson<T>(responseText);
      }
      return null;
    }

    if (options.doNotParse) return null;

    const text = await safeReadResponseText(response);
    return safeParseJson<T>(text);
  } catch (e) {
    if (e instanceof SessionExpiredError) throw e;
    if (!options.silent) {
      logApiError("General failure", { url, method, error: e, token });
    }
    return null;
  }
}

/**
 * Mutating-request variant that returns status info but not the parsed
 * body. Mirrors the legacy `apiStatus` / `apiStatusFetch`.
 */
export async function apiStatus(
  url: string,
  method: HttpMethod = "PUT",
  options: ApiFetchRawOptions & { handleError?: HandleErrorAlert } = {},
): Promise<ApiStatusResult> {
  const { token } = await resolveAccessToken(options.accessToken);

  try {
    const response = await apiFetchRaw(url, method, options);

    if (!response.ok) {
      const responseText = await safeReadResponseText(response);
      notifyNotAuthorized(response.status, responseText);
      reportAuthzDeniedResponse(response.status, responseText, url);
      logApiError("Request failed", {
        url,
        method,
        status: response.status,
        statusText: response.statusText,
        responseText,
        token,
      });
      if (options.handleError) {
        handleErrorAlert(responseText, response.status, options.handleError);
      }
      return {
        success: false,
        status: response.status,
        statusText: response.statusText,
      };
    }

    return {
      success: true,
      status: response.status,
      statusText: response.statusText,
    };
  } catch (e) {
    if (e instanceof SessionExpiredError) throw e;
    logApiError("General failure", { url, method, error: e, token });
    return { success: false, status: 500, statusText: "Internal Server Error" };
  }
}

/**
 * Status + parsed body. Mirrors the legacy `apiFetchWithStatus`. Use
 * when the caller needs both the HTTP outcome and the response data.
 */
export async function apiFetchWithStatus<T = unknown>(
  url: string,
  method: HttpMethod = "GET",
  options: ApiFetchRawOptions = {},
): Promise<ApiResponseWithStatus<T>> {
  const { token } = await resolveAccessToken(options.accessToken);

  try {
    const response = await apiFetchRaw(url, method, options);
    const text = await safeReadResponseText(response);
    if (!response.ok) {
      notifyNotAuthorized(response.status, text);
      reportAuthzDeniedResponse(response.status, text, url);
    }
    const data = safeParseJson<T>(text);
    return {
      success: response.ok,
      status: response.status,
      statusText: response.statusText,
      data,
    };
  } catch (e) {
    if (e instanceof SessionExpiredError) throw e;
    logApiError("General failure", { url, method, error: e, token });
    return { success: false, status: 0, statusText: "Network Error", data: null };
  }
}

/**
 * GET with optional localStorage caching. Cookie-based authentication
 * only (no `accessToken` parameter). Mirrors the legacy `apiLive`.
 */
export async function apiLive<T = unknown>(
  url: string,
  cacheKey?: CACHE_KEYS,
  cacheTtl?: number,
  fallback?: T,
  silent?: boolean,
): Promise<T | null> {
  if (cacheKey) {
    const cached: T | null = lscache.get(cacheKey);
    if (cached !== null && cached !== undefined) return cached;
  }

  const response = await apiFetch<T>(url, "GET", { fallback, silent });

  if (response && cacheKey) {
    lscache.set(cacheKey, response, cacheTtl);
  }
  return response;
}

// ---------------------------------------------------------------------------
// Error-alert formatting (ported from legacy api.ts)
// ---------------------------------------------------------------------------

export function showErrorAlert(
  errorResponse: GailError,
  defaultError: string,
  t: (val: string) => string,
  setAlert: (settings: MainAlertProps) => void,
): void {
  const errorsToShow: string[] = [];
  for (const error of errorResponse.errors) {
    const text = formatSingleError(error, t);
    if (text) errorsToShow.push(text);
  }
  setAlert({
    status: "error",
    message:
      errorsToShow.length > 0
        ? errorsToShow.join("\n")
        : ((errorResponse as { message?: string })?.message as string) ||
          t(defaultError),
    secondsToClose: 10,
  });
}

function formatSingleError(
  error: unknown,
  t: (val: string) => string,
): string | undefined {
  const code = (error as { code?: string })?.code;
  const title = (error as { title?: string })?.title;
  const detail = (error as { detail?: string })?.detail;
  const message = (error as { message?: string })?.message;

  if (code) {
    const translated = t(code);
    if (translated && translated !== code) return translated;
  }
  if (title && detail) return `${title}: ${detail}`;
  if (title) return title;
  if (detail) return detail;
  if (message) return message;
  if (code) return code;
  return undefined;
}

function handleErrorAlert(
  responseText: string,
  status: number,
  handle: HandleErrorAlert,
): void {
  // A refusal already raised its own alert. Falling through would both double up
  // and, since there is no `request_failed` translation to win over the detail,
  // print the raw marker body — scope names included — as user-facing copy.
  if (parseNotAuthorized(responseText)) return;

  const parsed = safeParseJson<Record<string, unknown>>(responseText);
  if (parsed && Array.isArray(parsed.errors)) {
    showErrorAlert(
      parsed as unknown as GailError,
      handle.defaultError,
      handle.t,
      handle.setAlert,
    );
  } else {
    showErrorAlert(
      {
        errors: [
          {
            code: "request_failed",
            title: "Request Failed",
            detail:
              responseText && responseText.trim().length > 0
                ? responseText
                : handle.defaultError,
            status: `${status}`,
          },
        ],
      },
      handle.defaultError,
      handle.t,
      handle.setAlert,
    );
  }
}

// Re-exported so callers don't double-import.
export type { AccessTokenOrAccessor, ApiFetchRawOptions, HttpMethod } from "./core";
